import { createResource, createSignal, For, Show } from "solid-js";
import type {
  ReportDefinition,
  ReportInput,
  ReportPeriod,
  ReportSchedule,
  ReportSnapshot,
  ReportTemplate,
} from "@energyplatform/client";
import { api } from "../services.ts";
import { t } from "../i18n.ts";
import { depthFirst, pathTo, scope, siteTree } from "../context.ts";
import { Icon } from "../icons.tsx";
import { ErrorBox } from "./ErrorBox.tsx";
import { ReportPaper } from "./ReportPaper.tsx";
import { Skeleton, useAction } from "./ui.tsx";

const STEPS = [
  "template",
  "scope",
  "sections",
  "format",
  "schedule",
  "preview",
  "save",
] as const;
const PERIODS: ReportPeriod[] = [
  "previous-month",
  "previous-week",
  "month-to-date",
  "year-to-date",
  "on-generate",
];
const SCHEDULES: ReportSchedule[] = [
  "manual",
  "weekly",
  "monthly",
  "quarterly",
];

/** The settable part of a definition, for editing and duplicating it. */
/** Templates whose figures have a comparison period. */
const COMPARED = new Set(["consumption", "costs", "reactive"]);

export function inputOf(r: ReportDefinition): ReportInput {
  return {
    name: r.name,
    template: r.template,
    nodeId: r.nodeId,
    period: r.period,
    comparison: r.comparison,
    sections: r.sections,
    formats: r.formats,
    landscape: r.landscape,
    schedule: r.schedule,
    recipients: r.recipients,
    askComment: r.askComment,
    blocks: r.blocks ?? [],
  };
}

/** Defaults of a new report from a template, for the scope place. */
export function newReport(
  template: ReportTemplate,
  nodeId: string,
): ReportInput {
  const node = siteTree()?.nodes.find((n) => n.id === nodeId);
  return {
    name: `${t(`reports.template.${template.key}.name`)} · ${node?.name ?? ""}`,
    template: template.key,
    nodeId,
    period:
      template.key === "costs" || template.key === "reactive"
        ? "previous-month"
        : "previous-week",
    comparison: COMPARED.has(template.key) ? "previous" : "none",
    sections: [...template.sections],
    formats: ["pdf"],
    landscape: false,
    schedule: "manual",
    recipients: [],
    askComment: template.key !== "custom",
    blocks: [],
  };
}

/**
 * Creates or edits a report in steps (prototype "Kreator raportu"): template, place and period, sections, format,
 * schedule and recipients, a preview from today's data, and saving — optionally generating the first snapshot.
 * Creating is kept apart from the snapshots, which are seen in the preview and history.
 */
export function ReportWizard(props: {
  templates: ReportTemplate[];
  report?: ReportDefinition;
  initial?: ReportInput;
  onCancel: () => void;
  onSaved: (report: ReportDefinition, snapshot: ReportSnapshot | null) => void;
}) {
  const ready = () => props.templates.filter((x) => x.ready);
  const [step, setStep] = createSignal(0);
  const [form, setForm] = createSignal<ReportInput>(
    (props.report && inputOf(props.report)) ??
      props.initial ??
      newReport(ready()[0]!, scope()?.id ?? siteTree()?.nodes[0]?.id ?? ""),
  );
  const set = (patch: Partial<ReportInput>) => setForm({ ...form(), ...patch });
  const [generateNow, setGenerateNow] = createSignal(true);
  const [problem, setProblem] = createSignal<string>();
  const [run, error, busy] = useAction();
  const [users] = createResource(() => api.events.users());
  const template = () => props.templates.find((x) => x.key === form().template);
  const nodes = () => depthFirst(siteTree()?.nodes ?? []);
  const path = (id: string) =>
    pathTo(siteTree()?.nodes ?? [], id)
      .map((n) => n.name)
      .join(" / ");
  const organisation = () =>
    siteTree()?.nodes.find((n) => n.parentId === null)?.name ?? "";

  const [preview] = createResource(
    () => (STEPS[step()] === "preview" ? JSON.stringify(form()) : undefined),
    () => api.reports.preview(form()),
  );

  const chooseTemplate = (x: ReportTemplate) => {
    const fresh = newReport(x, form().nodeId);
    setForm({ ...form(), ...fresh, name: fresh.name, blocks: form().blocks });
  };
  const blocks = () => form().blocks ?? [];
  const moveBlock = (i: number, by: number) => {
    const list = [...blocks()];
    const j = Math.max(0, Math.min(list.length - 1, i + by));
    const [b] = list.splice(i, 1);
    list.splice(j, 0, b!);
    set({ blocks: list });
  };
  const toggle = (list: string[], v: string, on: boolean) =>
    on ? [...new Set([...list, v])] : list.filter((x) => x !== v);

  const check = (): string | null => {
    const f = form();
    switch (STEPS[step()]) {
      case "scope":
        return f.name.trim() ? null : t("reports.wizard.needName");
      case "sections":
        return f.sections.length > 0 || (f.blocks ?? []).length > 0
          ? null
          : t("reports.wizard.needSection");
      case "format":
        return f.formats.length > 0 ? null : t("reports.wizard.needFormat");
      default:
        return null;
    }
  };
  const save = () =>
    void run(async () => {
      const input = { ...form(), name: form().name.trim() };
      const saved = props.report
        ? await api.reports.update(props.report.id, input)
        : await api.reports.create(input);
      const snapshot =
        generateNow() && input.period !== "on-generate"
          ? await api.reports.generate(saved.id)
          : null;
      props.onSaved(saved, snapshot);
    });
  const next = () => {
    const p = check();
    setProblem(p ?? undefined);
    if (!p) {
      if (step() === STEPS.length - 1) {
        save();
      } else {
        setStep(step() + 1);
      }
    }
  };

  return (
    <section class="card wzinline">
      <div class="dlg-h">
        <div>
          <div class="eyebrow">
            {t("reports.wizard.eyebrow")} ·{" "}
            {t("events.wizard.stepOf", {
              step: step() + 1,
              total: STEPS.length,
            })}
          </div>
          <h2>
            {props.report ? props.report.name : t("reports.wizard.title")}
          </h2>
        </div>
      </div>
      <ol class="wzbar">
        <For each={STEPS}>
          {(s, i) => (
            <li class={i() < step() ? "done" : i() === step() ? "cur" : ""}>
              <button
                type="button"
                disabled={i() > step()}
                onClick={() => setStep(i())}
              >
                {i() < step() ? "✓ " : ""}
                {t(`reports.wizard.step.${s}`)}
              </button>
            </li>
          )}
        </For>
      </ol>
      <div class="dlg-b" style={{ display: "grid", gap: "12px" }}>
        <Show when={STEPS[step()] === "template"}>
          <div
            class="cards"
            style={{
              "grid-template-columns": "repeat(auto-fill, minmax(210px, 1fr))",
            }}
          >
            <For each={ready()}>
              {(x) => (
                <label
                  class="rc"
                  style={{
                    cursor: "pointer",
                    ...(form().template === x.key
                      ? {
                          "border-color": "var(--accent)",
                          background: "var(--accent-soft)",
                        }
                      : {}),
                  }}
                >
                  <span
                    style={{
                      display: "flex",
                      gap: "8px",
                      "align-items": "center",
                    }}
                  >
                    <input
                      type="radio"
                      name="reportTemplate"
                      checked={form().template === x.key}
                      onChange={() => chooseTemplate(x)}
                    />
                    <b style={{ "font-weight": 600 }}>
                      {t(`reports.template.${x.key}.name`)}
                    </b>
                  </span>
                  <span class="hint">
                    {t(`reports.template.${x.key}.content`)}
                  </span>
                </label>
              )}
            </For>
          </div>
          <Show when={props.templates.some((x) => !x.ready)}>
            <p class="hint" style={{ margin: 0 }}>
              {t("reports.wizard.laterTemplates")}
            </p>
          </Show>
        </Show>
        <Show when={STEPS[step()] === "scope"}>
          <div class="field">
            <label for="rpN">{t("reports.wizard.name")}</label>
            <input
              id="rpN"
              value={form().name}
              maxlength={120}
              onInput={(e) => set({ name: e.currentTarget.value })}
            />
          </div>
          <div class="grid g-2e" style={{ gap: "10px" }}>
            <div class="field">
              <label for="rpS">{t("reports.wizard.scope")}</label>
              <select
                id="rpS"
                value={form().nodeId}
                onChange={(e) => set({ nodeId: e.currentTarget.value })}
              >
                <For each={nodes()}>
                  {(n) => <option value={n.node.id}>{path(n.node.id)}</option>}
                </For>
              </select>
            </div>
            <div class="field">
              <label for="rpP">{t("reports.wizard.period")}</label>
              <select
                id="rpP"
                value={form().period}
                onChange={(e) => {
                  const period = e.currentTarget.value as ReportPeriod;
                  set({
                    period,
                    schedule:
                      period === "on-generate" ? "manual" : form().schedule,
                  });
                }}
              >
                <For each={PERIODS}>
                  {(p) => <option value={p}>{t(`reports.period.${p}`)}</option>}
                </For>
              </select>
            </div>
          </div>
          <div class="grid g-2e" style={{ gap: "10px" }}>
            <div class="field">
              <label for="rpC">{t("reports.wizard.comparison")}</label>
              <select
                id="rpC"
                value={form().comparison}
                disabled={!COMPARED.has(form().template)}
                onChange={(e) =>
                  set({
                    comparison: e.currentTarget
                      .value as ReportInput["comparison"],
                  })
                }
              >
                <For each={["previous", "year", "none"] as const}>
                  {(c) => (
                    <option value={c}>{t(`reports.comparison.${c}`)}</option>
                  )}
                </For>
              </select>
            </div>
            <div class="field">
              <label for="rpM">{t("reports.wizard.media")}</label>
              <select id="rpM" value="el">
                <option value="el">{t("medium.el")}</option>
                <For each={["gas", "water"] as const}>
                  {(m) => (
                    <option value={m} disabled>
                      {t(`medium.${m}`)} — {t("reports.soon")}
                    </option>
                  )}
                </For>
              </select>
            </div>
          </div>
          <Show when={form().period === "on-generate"}>
            <p class="hint" style={{ margin: 0 }}>
              {t("reports.wizard.onGenerateHint")}
            </p>
          </Show>
        </Show>
        <Show when={STEPS[step()] === "sections"}>
          <div style={{ display: "grid", gap: 0 }}>
            <For each={template()?.sections ?? []}>
              {(s, i) => (
                <label class="check" style={{ padding: "7px 0" }}>
                  <input
                    type="checkbox"
                    checked={form().sections.includes(s)}
                    onChange={(e) =>
                      set({
                        sections: (template()?.sections ?? []).filter((x) =>
                          x === s
                            ? e.currentTarget.checked
                            : form().sections.includes(x),
                        ),
                      })
                    }
                  />
                  <span>
                    <span class="num hint">{i() + 1}.</span>{" "}
                    {t(`reports.section.${form().template}.${s}`)}
                  </span>
                </label>
              )}
            </For>
          </div>
          <div class="field">
            <span class="eyebrow">{t("reports.wizard.blocks")}</span>
            <Show
              when={blocks().length > 0}
              fallback={
                <p class="hint" style={{ margin: 0 }}>
                  {t("reports.wizard.noBlocks")}
                </p>
              }
            >
              <For each={blocks()}>
                {(b, i) => (
                  <div class="wz-block">
                    <span class="chip">{t(`reports.add.kinds.${b.kind}`)}</span>
                    <span class="nm">{b.title}</span>
                    <span class="hint">
                      {t("reports.wizard.blockSeries", {
                        count: b.series.length,
                      })}
                    </span>
                    <span
                      style={{
                        "margin-left": "auto",
                        display: "flex",
                        gap: "4px",
                      }}
                    >
                      <button
                        type="button"
                        class="btn ghost sm"
                        aria-label={t("dashboard.up")}
                        onClick={() => moveBlock(i(), -1)}
                      >
                        ↑
                      </button>
                      <button
                        type="button"
                        class="btn ghost sm"
                        aria-label={t("dashboard.down")}
                        onClick={() => moveBlock(i(), 1)}
                      >
                        ↓
                      </button>
                      <button
                        type="button"
                        class="btn ghost sm"
                        aria-label={t("reports.wizard.removeBlock")}
                        onClick={() =>
                          set({ blocks: blocks().filter((x) => x.id !== b.id) })
                        }
                      >
                        <Icon name="x" />
                      </button>
                    </span>
                  </div>
                )}
              </For>
            </Show>
          </div>
          <div class="grid g-2e" style={{ gap: "10px" }}>
            <div class="field">
              <label for="rpT">{t("reports.wizard.text")}</label>
              <select
                id="rpT"
                value={form().askComment ? "ask" : "none"}
                onChange={(e) =>
                  set({ askComment: e.currentTarget.value === "ask" })
                }
              >
                <option value="ask">{t("reports.wizard.askComment")}</option>
                <option value="none">{t("reports.wizard.noComment")}</option>
              </select>
            </div>
          </div>
          <p class="hint" style={{ margin: 0 }}>
            {t("reports.wizard.footerAlways")}
          </p>
        </Show>
        <Show when={STEPS[step()] === "format"}>
          <div class="field">
            <span class="eyebrow">{t("reports.wizard.formats")}</span>
            <div style={{ display: "flex", gap: "16px", "flex-wrap": "wrap" }}>
              <For each={["pdf", "csv", "xlsx"] as const}>
                {(x) => (
                  <label
                    style={{
                      display: "inline-flex",
                      gap: "6px",
                      "align-items": "center",
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={form().formats.includes(x)}
                      onChange={(e) =>
                        set({
                          formats: toggle(
                            form().formats,
                            x,
                            e.currentTarget.checked,
                          ),
                        })
                      }
                    />
                    {x.toUpperCase()}
                  </label>
                )}
              </For>
            </div>
          </div>
          <div class="grid g-2e" style={{ gap: "10px" }}>
            <div class="field">
              <label for="rpO">{t("reports.wizard.layout")}</label>
              <select
                id="rpO"
                value={form().landscape ? "landscape" : "portrait"}
                onChange={(e) =>
                  set({ landscape: e.currentTarget.value === "landscape" })
                }
              >
                <option value="portrait">{t("reports.wizard.portrait")}</option>
                <option value="landscape">
                  {t("reports.wizard.landscape")}
                </option>
              </select>
            </div>
            <div class="field" style={{ opacity: 0.6 }}>
              <label for="rpL">{t("reports.wizard.language")}</label>
              <select id="rpL" disabled>
                <option>{t("reports.wizard.languageReader")}</option>
              </select>
            </div>
          </div>
          <p class="hint" style={{ margin: 0 }}>
            {t("reports.wizard.formatHint")}
          </p>
        </Show>
        <Show when={STEPS[step()] === "schedule"}>
          <div class="field">
            <label for="rpH">{t("reports.wizard.schedule")}</label>
            <select
              id="rpH"
              value={form().schedule}
              onChange={(e) =>
                set({ schedule: e.currentTarget.value as ReportSchedule })
              }
            >
              <For each={SCHEDULES}>
                {(s) => (
                  <option
                    value={s}
                    disabled={s !== "manual" && form().period === "on-generate"}
                  >
                    {t(`reports.schedule.${s}`)}
                  </option>
                )}
              </For>
            </select>
          </div>
          <div>
            <div class="eyebrow" style={{ "margin-bottom": "6px" }}>
              {t("reports.wizard.recipients")}
            </div>
            <Show
              when={(users() ?? []).length > 0}
              fallback={<p class="hint">{t("reports.wizard.noUsers")}</p>}
            >
              <For each={users() ?? []}>
                {(u) => (
                  <label class="check" style={{ padding: "6px 0" }}>
                    <input
                      type="checkbox"
                      checked={form().recipients.includes(u.id)}
                      onChange={(e) =>
                        set({
                          recipients: toggle(
                            form().recipients,
                            u.id,
                            e.currentTarget.checked,
                          ),
                        })
                      }
                    />
                    <span>
                      {u.name}
                      <Show when={u.email}>
                        <span class="hint"> · {u.email}</span>
                      </Show>
                    </span>
                  </label>
                )}
              </For>
            </Show>
          </div>
          <div class="field">
            <label for="rpK">{t("reports.wizard.delivery")}</label>
            <select id="rpK" value="app">
              <option value="app">{t("reports.wizard.deliveryApp")}</option>
              <option value="mail" disabled>
                {t("reports.wizard.deliveryMail")} — {t("reports.soon")}
              </option>
              <option value="link" disabled>
                {t("reports.wizard.deliveryLink")} — {t("reports.soon")}
              </option>
            </select>
          </div>
          <p class="hint" style={{ margin: 0 }}>
            {t("reports.wizard.recipientsHint")}
          </p>
        </Show>
        <Show when={STEPS[step()] === "preview"}>
          <div
            style={{
              background: "var(--sunken)",
              padding: "14px",
              "border-radius": "8px",
              "min-width": 0,
            }}
          >
            <Show
              when={!preview.error}
              fallback={<ErrorBox error={preview.error} />}
            >
              <Show when={preview()} fallback={<Skeleton lines={8} />}>
                {(p) => (
                  <ReportPaper
                    name={form().name}
                    organisation={organisation()}
                    from={p().from}
                    to={p().to}
                    content={p().content}
                    mini
                  />
                )}
              </Show>
            </Show>
          </div>
          <p class="hint" style={{ margin: 0 }}>
            {t("reports.wizard.previewHint")}
          </p>
        </Show>
        <Show when={STEPS[step()] === "save"}>
          <table class="tbl sumtbl">
            <tbody>
              <tr>
                <td>{t("reports.wizard.sumReport")}</td>
                <td>
                  <b style={{ "font-weight": 500 }}>{form().name}</b>
                </td>
              </tr>
              <tr>
                <td>{t("reports.wizard.sumScope")}</td>
                <td>
                  {path(form().nodeId)} · {t(`reports.period.${form().period}`)}
                </td>
              </tr>
              <tr>
                <td>{t("reports.wizard.sumSections")}</td>
                <td>
                  {form().sections.length}
                  {blocks().length > 0
                    ? ` + ${t("reports.wizard.blockCount", { count: blocks().length })}`
                    : ""}
                </td>
              </tr>
              <tr>
                <td>{t("reports.wizard.formats")}</td>
                <td>
                  {form()
                    .formats.map((x) => x.toUpperCase())
                    .join(", ")}
                </td>
              </tr>
              <tr>
                <td>{t("reports.wizard.sumSchedule")}</td>
                <td>
                  {t(`reports.schedule.${form().schedule}`)} ·{" "}
                  {t("reports.recipientCount", {
                    count: form().recipients.length,
                  })}
                </td>
              </tr>
            </tbody>
          </table>
          <label class="opt">
            <input
              type="checkbox"
              checked={generateNow() && form().period !== "on-generate"}
              disabled={form().period === "on-generate"}
              onChange={(e) => setGenerateNow(e.currentTarget.checked)}
            />
            <div>
              <b>{t("reports.wizard.generateNow")}</b>
              <small>
                {form().period === "on-generate"
                  ? t("reports.wizard.generateLater")
                  : t("reports.wizard.generateNowHint")}
              </small>
            </div>
          </label>
        </Show>
        <Show when={problem()}>
          <p
            class="chip warn"
            role="alert"
            style={{ margin: 0, "justify-self": "start" }}
          >
            {problem()}
          </p>
        </Show>
        <Show when={error()}>
          <ErrorBox error={error()} />
        </Show>
      </div>
      <div class="dlg-f">
        <Show
          when={step() > 0}
          fallback={
            <button type="button" class="btn" onClick={() => props.onCancel()}>
              {t("common.cancel")}
            </button>
          }
        >
          <button type="button" class="btn" onClick={() => setStep(step() - 1)}>
            <Icon name="chevl" />
            {t("common.back")}
          </button>
        </Show>
        <button
          type="button"
          class="btn primary"
          disabled={busy()}
          onClick={next}
        >
          {step() === STEPS.length - 1
            ? t("reports.wizard.save")
            : t("common.next")}
          <Show when={step() < STEPS.length - 1}>
            <Icon name="chev" />
          </Show>
        </button>
      </div>
    </section>
  );
}
